import { updatePaymentMethod } from "@/api/rest/checkoutApi";
import {
  OutlineButton,
  SolidButton,
} from "@/components/StoreComponents/StoreButton";
import { useAppSelector } from "@/redux/hooks";
import { TABLET_SCREEN_SIZE } from "@brandclub/common-ui";
import { Checkbox, Collapse, styled, useTheme } from "@mui/material";

import AddressForm, {
  BillingAddressCheckbox,
} from "../Checkout/components/CheckoutV2/CheckoutForm/components/shipping/AddressForm";

import { Address } from "../Checkout/components/CheckoutV2/types/CheckoutIntent";

import {
  ExpressCheckoutElement,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import {
  LineItem,
  PaymentMethod,
  ShippingRate,
  StripeError,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementShippingAddressChangeEvent,
  StripeExpressCheckoutElementShippingRateChangeEvent,
} from "@stripe/stripe-js";
import { useState } from "react";
// Removed dependency on CheckoutV2 AddressForm to avoid requiring CheckoutUIProvider
import { CheckoutIntent } from "../Checkout/components/CheckoutV2/types/CheckoutIntent";
import { StoreError } from "../StoreComponents/StoreError";

// Add global declaration for the Google Pay sheet on window
declare global {
  interface Window {
    gpay?: {
      close: () => void;
    };
  }
}
// // Helper to detect Safari browser
// const isSafari = () => {
//   const ua = navigator.userAgent.toLowerCase();
//   return ua.indexOf("safari") !== -1 && ua.indexOf("chrome") === -1;
// };

const Container = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));

export const enum ElementType {
  expressCheckout = "expressCheckout",
  card = "card",
}

const useStripePaymentProcess = (
  elementType: ElementType,
  opts?: {
    checkoutIntentId?: string;
    onCheckoutIntentUpdated?: (ci: CheckoutIntent) => void;
  }
) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentMethodError, setPaymentMethodError] =
    useState<string | null>(null);

  const createPaymentMethod = async () => {
    if (!stripe || !elements) {
      return {
        error: new Error("Stripe or elements not initialized"),
        paymentMethod: null,
        isProcessing: false,
      };
    }

    setIsProcessing(true);
    setPaymentMethodError(null);

    try {
      // Submit the form first
      const submitResult = await elements.submit();
      if (submitResult.error) {
        throw new Error(
          submitResult.error.message || "Failed to submit payment details"
        );
      }

      console.log(
        "[Stripe Payment] Creating payment method (backend will attach to customer)"
      );

      let error: StripeError | undefined = undefined;
      let paymentMethod: PaymentMethod | undefined = undefined;
      if (elementType === ElementType.expressCheckout) {
        // Create payment method (backend will attach to customer)
        ({ error, paymentMethod } = await stripe.createPaymentMethod({
          elements,
        }));
      } else {
        // We have to do it this way because there is a bug in Stripe
        // that will not let a user add a card after using the express checkout in the same session
        ({ error, paymentMethod } = await stripe.createPaymentMethod({
          element: elements.getElement("payment")!,
        }));
      }

      console.log(
        "[Stripe Payment] Payment method created:",
        paymentMethod?.id,
        "customer:",
        paymentMethod?.customer || "null (backend will attach)"
      );

      if (error) {
        throw new Error(error.message || "Failed to create payment method");
      }

      if (!paymentMethod) {
        throw new Error("No payment method created");
      }

      return {
        paymentMethod,
        error: null,
        isProcessing: false,
      };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "An unexpected error occurred";
      setPaymentMethodError(errorMessage);
      return {
        error: error instanceof Error ? error : new Error(errorMessage),
        paymentMethod: null,
        isProcessing: false,
      };
    } finally {
      setIsProcessing(false);
    }
  };

  const processPayment = async ({
    paymentMethod,
    shippingAddress,
    billingDetails,
  }: {
    paymentMethod: PaymentMethod;
    shippingAddress?: CheckoutIntent["shippingAddress"];
    billingDetails?: CheckoutIntent["billingAddress"];
  }) => {
    try {
      setIsProcessing(true);
      const checkoutIntentId = opts?.checkoutIntentId;

      // console.log('[Stripe Payment] Processing payment with checkoutIntentId:', checkoutIntentId);

      if (checkoutIntentId) {
        const {
          checkoutIntent: updatedCheckoutIntent,
          status,
          errorMessage,
        } = await updatePaymentMethod({
          checkoutIntentId,
          paymentMethodId: paymentMethod.id,
          shippingAddress: shippingAddress as any,
          billingAddress: billingDetails as any,
        });

        console.log("[Stripe Payment] Update payment method response:", {
          status,
          hasCheckoutIntent: Boolean(updatedCheckoutIntent),
          availablePaymentMethodsCount:
            updatedCheckoutIntent?.availablePaymentMethods?.length || 0,
        });

        if (status === "success") {
          if (updatedCheckoutIntent) {
            opts?.onCheckoutIntentUpdated?.(
              updatedCheckoutIntent as CheckoutIntent
            );
          } else {
            console.warn(
              "[Stripe Payment] No checkoutIntent available, skipping payment processing"
            );
          }
          return true;
        } else {
          setPaymentMethodError(
            errorMessage || "Failed to update payment method"
          );
          return false;
        }
      } else {
        console.warn(
          "[Stripe Payment] No checkoutIntentId available, skipping payment processing"
        );
        return true;
      }
    } catch (error) {
      console.error("[Stripe Payment] Error processing payment:", error);
      setPaymentMethodError(
        error instanceof Error ? error.message : "An unexpected error occurred"
      );
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    createPaymentMethod,
    processPayment,
    isProcessing,
    paymentMethodError,
    clearPaymentMethodError: () => setPaymentMethodError(null),
  };
};

export const StripeExpressCheckout = ({
  onSuccess,
  onError,
  onShippingAddressChange,
  onShippingRateChange,
  shippingRates,
  lineItems,
  checkoutIntentIdOverride,
  onCheckoutIntentUpdated,
}: {
  onSuccess: (paymentMethod: PaymentMethod) => void;
  onError: (error: Error) => void;
  onShippingAddressChange: (
    event: StripeExpressCheckoutElementShippingAddressChangeEvent
  ) => void;
  onShippingRateChange: (
    event: StripeExpressCheckoutElementShippingRateChangeEvent
  ) => void;
  shippingRates: ShippingRate[];
  lineItems: LineItem[];
  checkoutIntentIdOverride?: string;
  onCheckoutIntentUpdated?: (ci: CheckoutIntent) => void;
}) => {
  const {
    createPaymentMethod,
    processPayment,
    paymentMethodError,
    clearPaymentMethodError,
  } = useStripePaymentProcess(ElementType.expressCheckout, {
    checkoutIntentId: checkoutIntentIdOverride,
    onCheckoutIntentUpdated,
  });

  const onConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    try {
      clearPaymentMethodError();
      // console.log("stripe express checkout onConfirm event", event);
      const result = await createPaymentMethod();
      if (result.error) {
        onError(result.error);
        return;
      }
      if (result.paymentMethod) {
        const shippingAddress = event.shippingAddress;
        const billingDetails = event.billingDetails;
        let shippingAddressToSubmit: CheckoutIntent["shippingAddress"] =
          undefined;
        if (shippingAddress && shippingAddress.address.line1) {
          shippingAddressToSubmit = {
            address1: shippingAddress.address.line1,
            address2: shippingAddress.address.line2 ?? "",
            city: shippingAddress.address.city,
            state: shippingAddress.address.state,
            zipCode: shippingAddress.address.postal_code,
            country: shippingAddress.address.country,
            firstName: shippingAddress.name.split(" ")[0],
            lastName: shippingAddress.name.split(" ")[1],
            phoneNumber: billingDetails?.phone ?? "",
            email: billingDetails?.email ?? "",
          };
        }
        let billingAddressToSubmit: CheckoutIntent["billingAddress"] =
          undefined;
        if (billingDetails && billingDetails.address.line1) {
          billingAddressToSubmit = {
            address1: billingDetails.address.line1,
            address2: billingDetails.address.line2 ?? "",
            city: billingDetails.address.city,
            state: billingDetails.address.state,
            zipCode: billingDetails.address.postal_code,
            country: billingDetails.address.country,
            firstName: billingDetails.name.split(" ")[0],
            lastName: billingDetails.name.split(" ")[1],
            email: billingDetails.email,
            phoneNumber: billingDetails.phone,
          };
        }

        if (
          await processPayment({
            paymentMethod: result.paymentMethod,
            shippingAddress: shippingAddressToSubmit,
            billingDetails: billingAddressToSubmit,
          })
        ) {
          onSuccess(result.paymentMethod);
        }
      }
    } catch (error) {
      onError(error as Error);
    }
  };

  return (
    <Container className="checkout-express-payment-container">
      <ExpressCheckoutElement
        className="express-checkout-element"
        options={{
          business: {
            name: "Brandclub",
          },
          buttonType: {
            applePay: "buy",
            googlePay: "buy",
          },
          buttonTheme: {
            applePay: "white-outline",
            googlePay: "white",
          },
          emailRequired: true,
          phoneNumberRequired: true,
          shippingAddressRequired: true,
          billingAddressRequired: true,
          paymentMethods: {
            applePay: "always",
            googlePay: "always",
          },
          shippingRates,
          lineItems,
        }}
        onShippingAddressChange={onShippingAddressChange}
        onShippingRateChange={onShippingRateChange}
        onConfirm={onConfirm}
      />

      {paymentMethodError && (
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            marginBottom: "16px",
          }}
        >
          <StoreError
            errorMessage={paymentMethodError}
            sx={{ textAlign: "center", width: "fit-content" }}
          />
        </div>
      )}
    </Container>
  );
};

const CardCheckoutContainer = styled("div")(({ theme }) => ({
  padding: "0 20px 20px 20px",
  display: "flex",
  flexDirection: "column",
  width: 550,
  boxSizing: "border-box",
  maxWidth: "100%",

  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    padding: "20px 20px 20px 20px",
  },

  ".payment_form_title": {
    fontSize: "16px",
    fontWeight: 600,
    color: theme.vars.palette.text.primary,
    marginBottom: "16px",
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      textAlign: "center",
    },
  },

  ".buttonGroup": {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "22px",
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      marginTop: "12px",
      gap: "12px",
      flexDirection: "column-reverse",
    },
  },
}));

const BillingAddress = ({
  address,
  sameAsShipping,
  onAddressChange,
  onSameAsShippingChange,
  hasShippingAddress,
}: {
  address: Partial<Address>;
  sameAsShipping: boolean;
  onAddressChange: (address: Partial<Address>) => void;
  onSameAsShippingChange: (sameAsShipping: boolean) => void;
  hasShippingAddress: boolean;
}) => {
  // When there's no shipping address, the collapse should always be open
  const shouldShowCollapse = hasShippingAddress ? !sameAsShipping : true;

  // Custom checkbox for when there's no shipping address
  const CustomBillingAddressCheckbox = () => {
    const theme = useTheme();
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      onSameAsShippingChange(event.target.checked);
    };

    return (
      <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
        <Checkbox
          sx={{
            "&:hover": { bgcolor: "transparent" },
            padding: "0px",
            paddingRight: "7px",
          }}
          disableRipple
          color="default"
          checked={sameAsShipping}
          onChange={handleChange}
        />
        <span style={{ color: theme.vars.palette.text.primary, fontSize: 14 }}>
          {hasShippingAddress
            ? "My billing and shipping address are the same"
            : "Use this address for shipping as well"}
        </span>
      </div>
    );
  };

  return (
    <div style={{ marginTop: "16px" }}>
      {hasShippingAddress ? (
        <BillingAddressCheckbox
          useShippingAddress={sameAsShipping}
          onUseShippingAddressChange={onSameAsShippingChange}
        />
      ) : (
        <CustomBillingAddressCheckbox />
      )}
      <Collapse in={shouldShowCollapse}>
        <div style={{ marginTop: "24px" }}>
          <AddressForm value={address} onChange={onAddressChange} />
        </div>
      </Collapse>
    </div>
  );
};

export const StripeCardCheckout = ({
  onSuccess,
  onError,
  onCancel,
  checkoutIntentIdOverride,
  checkoutIntentOverride,
  onCheckoutIntentUpdated,
}: {
  onCancel: () => void;
  onSuccess: (paymentMethod: PaymentMethod) => void;
  onError: (error: Error) => void;
  onShippingAddressChange: (
    event: StripeExpressCheckoutElementShippingAddressChangeEvent
  ) => void;
  onShippingRateChange: (
    event: StripeExpressCheckoutElementShippingRateChangeEvent
  ) => void;
  shippingRates: ShippingRate[];
  checkoutIntentIdOverride?: string;
  checkoutIntentOverride?: CheckoutIntent;
  onCheckoutIntentUpdated?: (ci: CheckoutIntent) => void;
}) => {
  const user = useAppSelector((state) => state.userProfile);
  const {
    createPaymentMethod,
    processPayment,
    isProcessing,
    paymentMethodError,
    clearPaymentMethodError,
  } = useStripePaymentProcess(ElementType.card, {
    checkoutIntentId: checkoutIntentIdOverride,
    onCheckoutIntentUpdated,
  });
  const [isPaymentMethodLoaded, setIsPaymentMethodLoaded] = useState(false);
  const [isPaymentMethodCompleted, setPaymentMethodCompleted] = useState(false);

  const hasShippingAddress = Boolean(checkoutIntentOverride?.shippingAddress);

  // Get current values from checkout intent
  // If there's a shipping address, default to true (checked by default)
  // If there's no shipping address, default to false so the form is always visible
  const [sameAsShipping, setSameAsShipping] = useState(
    hasShippingAddress ? true : false
  );
  const [billingAddress, setBillingAddress] = useState<Partial<Address>>(
    checkoutIntentOverride?.billingAddress || {
      address1: "",
      address2: "",
      city: "",
      state: "",
      zipCode: "",
      country: "",
      firstName: "",
      lastName: "",
      phoneNumber: "",
      email: "",
    }
  );

  const isBillingAddressComplete = sameAsShipping
    ? true
    : Boolean(
        (billingAddress.firstName || "").trim() &&
          (billingAddress.lastName || "").trim() &&
          (billingAddress.address1 || "").trim() &&
          (billingAddress.city || "").trim() &&
          (billingAddress.state || "").trim() &&
          (billingAddress.zipCode || "").trim()
      );

  // Handle checkbox change with custom logic for when there's no shipping address
  const handleSameAsShippingChange = async (isSameAsShipping: boolean) => {
    setSameAsShipping(isSameAsShipping);
  };

  // Note: For standalone add-card, we rely on Stripe PaymentElement to collect
  // billing details. We do not render an extra billing address form here.

  const handlePaymentSubmit = async () => {
    try {
      const result = await createPaymentMethod();
      clearPaymentMethodError();
      if (result.error) {
        onError(result.error);
        return;
      }
      if (result.paymentMethod) {
        if (hasShippingAddress) {
          await processPayment({
            paymentMethod: result.paymentMethod,
            billingDetails: sameAsShipping
              ? checkoutIntentOverride?.billingAddress
              : (billingAddress as CheckoutIntent["billingAddress"]),

            shippingAddress: undefined,
          });
        } else {
          await processPayment({
            paymentMethod: result.paymentMethod,
            billingDetails: billingAddress as CheckoutIntent["billingAddress"],

            shippingAddress: sameAsShipping
              ? (billingAddress as CheckoutIntent["billingAddress"])
              : undefined,
          });
        }

        onSuccess(result.paymentMethod);
      }
    } catch (error) {
      onError(error as Error);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <CardCheckoutContainer className="stripe-card-checkout-container">
      <div className="payment_form_title">Add new credit card</div>
      <PaymentElement
        onReady={() => {
          setIsPaymentMethodLoaded(true);
        }}
        onChange={(event) => {
          setPaymentMethodCompleted(event.complete);
        }}
        options={{
          fields: {
            billingDetails: {
              name: "auto",
              email: "auto",
              phone: "auto",
              address: {
                country: "auto",
                postalCode: "auto",
                state: "auto",
                city: "auto",
                line1: "auto",
                line2: "auto",
              },
            },
          },
          wallets: {
            applePay: "never",
            googlePay: "never",
          },
          defaultValues: {
            billingDetails: {
              name: `${user.given_name} ${user.family_name}`,
              email: user.email,
              phone: user.phoneNumber,
            },
          },
        }}
      />
      {paymentMethodError && (
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            marginBottom: "16px",
          }}
        >
          <StoreError
            errorMessage={paymentMethodError}
            sx={{ textAlign: "center", width: "fit-content" }}
          />
        </div>
      )}

      <BillingAddress
        address={billingAddress}
        sameAsShipping={sameAsShipping}
        onAddressChange={setBillingAddress}
        onSameAsShippingChange={handleSameAsShippingChange}
        hasShippingAddress={hasShippingAddress}
      />

      <div className="buttonGroup">
        <OutlineButton
          height={{ xs: 37 }}
          sx={{
            width: "unset",
            minWidth: "83px",
            [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
              width: "100%",
              maxWidth: "100%",
            },
          }}
          onClick={onCancel}
          disabled={isProcessing || !isPaymentMethodLoaded}
        >
          Cancel
        </OutlineButton>
        <SolidButton
          height={{ xs: 37 }}
          sx={{
            width: "unset",
            minWidth: "83px",
            [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
              width: "100%",
              maxWidth: "100%",
            },
          }}
          onClick={handlePaymentSubmit}
          disabled={
            isProcessing ||
            !isPaymentMethodLoaded ||
            !isPaymentMethodCompleted ||
            !isBillingAddressComplete
          }
        >
          {isProcessing ? "Saving" : "Save"}
        </SolidButton>
      </div>
    </CardCheckoutContainer>
  );
};

export const StripeCheckout = ({
  onSuccess,
  onError,
  onShippingAddressChange,
  onShippingRateChange,
  shippingRates,
  lineItems,
  onCancel,
  checkoutIntentIdOverride,
  checkoutIntentOverride,
  onCheckoutIntentUpdated,
}: {
  onCancel: () => void;
  onSuccess: (paymentMethodType: string, paymentMethod: PaymentMethod) => void;
  onError: (error: Error) => void;
  onShippingAddressChange: (
    event: StripeExpressCheckoutElementShippingAddressChangeEvent
  ) => void;
  onShippingRateChange: (
    event: StripeExpressCheckoutElementShippingRateChangeEvent
  ) => void;
  shippingRates: ShippingRate[];
  lineItems: LineItem[];
  checkoutIntentIdOverride?: string;
  checkoutIntentOverride?: CheckoutIntent;
  onCheckoutIntentUpdated?: (ci: CheckoutIntent) => void;
}) => {
  const user = useAppSelector((state) => state.userProfile);

  if (!user) {
    return null;
  }

  return (
    <>
      <div style={{ marginBottom: "20px" }}>
        <StripeExpressCheckout
          onSuccess={(paymentMethod) => onSuccess("express", paymentMethod)}
          onError={onError}
          onShippingAddressChange={onShippingAddressChange}
          onShippingRateChange={onShippingRateChange}
          shippingRates={shippingRates}
          lineItems={lineItems}
          checkoutIntentIdOverride={checkoutIntentIdOverride}
          onCheckoutIntentUpdated={onCheckoutIntentUpdated}
        />
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          margin: "20px 0",
          color: "#666",
        }}
      >
        <div
          style={{
            flex: 1,
            height: "1px",
            backgroundColor: "#dcdddc",
          }}
        />
        <span
          style={{
            margin: "0 16px",
            fontSize: "14px",
          }}
        >
          Add new credit card
        </span>
        <div
          style={{
            flex: 1,
            height: "1px",
            backgroundColor: "#dcdddc",
          }}
        />
      </div>
      <div>
        <StripeCardCheckout
          onSuccess={(paymentMethod) => onSuccess("card", paymentMethod)}
          onError={onError}
          onShippingAddressChange={onShippingAddressChange}
          onShippingRateChange={onShippingRateChange}
          shippingRates={shippingRates}
          onCancel={onCancel}
          checkoutIntentIdOverride={checkoutIntentIdOverride}
          checkoutIntentOverride={checkoutIntentOverride}
          onCheckoutIntentUpdated={onCheckoutIntentUpdated}
        />
      </div>
    </>
  );
};
